import React, { useState, useEffect } from 'react'

import { useRouter } from 'next/router'
import useAuthenticationApi from '@hooks/useAuthenticationApi'
import { getXCLEARCorrelationInfo } from '@api/utils'
import { Enrollment as EnrollmentClient } from '@clear-denver/tpc-openapi-typescript'
import { DiscreteEvent, dispatchCustomEvent, KPI } from '@utils/customEvents'
import { useForm } from 'react-hook-form'
import { yupResolver } from '@hookform/resolvers/yup'
import { authSchema } from '@validators/schema/enroll/authSchema'
import { useEnrollmentContext } from '@contexts/EnrollmentContext'
import { trackFunnelStep } from '@utils/trackFunnelStep'
import AuthFlow from '@components/pages/auth/AuthFlow'
import { useFlag } from '@hooks/useFlag'

export interface FormData extends Partial<EnrollmentClient.BiographicsLookup> {
  otpSessionId: string
}

export type InitiateAuth = {
  phoneNumber?: string
  email?: string
  otpSessionId: string
}

export enum FormSteps {
  CONTACT = 'Contact',
  VERIFICATION = 'Verification',
}

const AuthPage: React.FC = () => {
  const router = useRouter()
  const { client: authenticationClient } = useAuthenticationApi()
  const { setBiographicAuthInfo, setApptInfo } = useEnrollmentContext()
  const displayAuthenticationRevisedLayout =
    useFlag('layoutRefresh')?.displayAuthenticationRevisedLayout

  const [verificationFormData, setVerificationFormData] =
    useState<FormData | null>(null)
  const { register, handleSubmit, formState } = useForm<any>({
    resolver: yupResolver(authSchema),
    mode: 'onChange',
  })

  useEffect(() => {
    setApptInfo(null)
  }, [setApptInfo])

  const [step, setStep] = useState<FormSteps>(FormSteps.CONTACT)
  const { errors, isValid } = formState
  const [otpFailureMessage, setOtpFailureMessage] = useState<string>('')
  const [isLoading, setIsLoading] = useState<boolean>(false)
  const desiredApply = router.query?.desiredPath === '/enroll/you'
  const desiredRenew =
    router.query?.desiredPath === '/renewal/eligibility/eligibility'
  useEffect(() => {
    if (desiredApply) {
      trackFunnelStep('apply', 2, 'auth')
    }
    if (desiredRenew) {
      trackFunnelStep('renew', 2, 'auth')
    }
  }, [desiredApply, desiredRenew])

  async function onSubmitContact(formData: InitiateAuth) {
    setIsLoading(true)
    const { phoneNumber, email } = formData
    const xCLEARCorrelationInfo = getXCLEARCorrelationInfo()
    const baseMetaData = {
      kpi: KPI.USER_INTERACTION,
      xCLEARCorrelationInfo,
      url: router.route,
    }
    const formatedPhoneNumber = phoneNumber ? `+1.${phoneNumber}` : null
    dispatchCustomEvent(EnrollmentClient.LogEntryLevelEnum.INFO, {
      payload: {
        message: 'Initiate auth begin',
      },
      metadata: {
        ...baseMetaData,
        event: DiscreteEvent.INITIATE_AUTH_BEGIN,
      },
    })
    try {
      const response = await authenticationClient.initiateAuth({
        xCLEARCorrelationInfo,
        otpForm: {
          phoneNumber: formatedPhoneNumber,
          emailAddress: email,
        },
      })
      setBiographicAuthInfo({
        phoneNumber: formatedPhoneNumber,
        emailAddress: email,
      })
      if (response?.otpSessionId) {
        setVerificationFormData({
          ...formData,
          otpSessionId: response.otpSessionId,
        })
        setStep(FormSteps.VERIFICATION)
      }
      dispatchCustomEvent(EnrollmentClient.LogEntryLevelEnum.INFO, {
        payload: {
          message: 'Initiate auth successful',
        },
        metadata: {
          ...baseMetaData,
          event: DiscreteEvent.INITIATE_AUTH_SUCCESS,
        },
      })
      setIsLoading(false)
    } catch (error) {
      setIsLoading(false)
      dispatchCustomEvent(EnrollmentClient.LogEntryLevelEnum.ERROR, {
        payload: {
          message: error?.message,
          stack: error?.stack,
        },
        metadata: {
          ...baseMetaData,
          event: DiscreteEvent.INITIATE_AUTH_FAILURE,
        },
      })
    }
  }

  async function onVerificationSubmit(formData) {
    setIsLoading(true)
    const xCLEARCorrelationInfo = getXCLEARCorrelationInfo()
    const baseMetaData = {
      kpi: KPI.USER_INTERACTION,
      xCLEARCorrelationInfo,
      url: router.route,
    }
    dispatchCustomEvent(EnrollmentClient.LogEntryLevelEnum.INFO, {
      payload: {
        message: 'OTP verification begin',
      },
      metadata: {
        ...baseMetaData,
        event: DiscreteEvent.OTP_VERIFICATION_BEGIN,
      },
    })
    try {
      await authenticationClient.otpChallenge({
        xCLEARCorrelationInfo,
        otpCode: {
          otpSessionId: verificationFormData.otpSessionId,
          otpCode: formData.verificationCode,
        },
      })
      if (router.query?.desiredPath) {
        router.push(
          {
            pathname: router.query?.desiredPath as string,
            query: {
              otpSessionId: verificationFormData?.otpSessionId,
            },
          },
          router.query?.desiredPath as string
        )
      } else {
        router.push('/')
      }

      dispatchCustomEvent(EnrollmentClient.LogEntryLevelEnum.INFO, {
        payload: {
          message: 'OTP verification successful',
        },
        metadata: {
          ...baseMetaData,
          event: DiscreteEvent.OTP_VERIFICATION_SUCCESS,
        },
      })
      setIsLoading(false)
    } catch (error) {
      setIsLoading(false)
      setOtpFailureMessage(
        'Invalid verification code provided, please try again.'
      )
      dispatchCustomEvent(EnrollmentClient.LogEntryLevelEnum.ERROR, {
        payload: {
          message: error?.message,
          stack: error?.stack,
        },
        metadata: {
          ...baseMetaData,
          event: DiscreteEvent.OTP_VERIFICATION_FAILURE,
        },
      })
    }
  }

  useEffect(() => {
    document.documentElement.style.backgroundColor = '#FBFBF9'
    return () => {
      document.documentElement.style.backgroundColor = ''
    }
  }, [])

  return (
    <AuthFlow
      displayAuthenticationRevisedLayout={displayAuthenticationRevisedLayout}
      step={step}
      verificationFormData={verificationFormData}
      onVerificationSubmit={onVerificationSubmit}
      otpFailureMessage={otpFailureMessage}
      setOtpFailureMessage={setOtpFailureMessage}
      isLoading={isLoading}
      handleSubmit={handleSubmit}
      onSubmitContact={onSubmitContact}
      register={register}
      errors={errors}
      isValid={isValid}
    />
  )
}

export default AuthPage
